import { useRef, useEffect, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Award, Globe, Clock, Target, X, ZoomIn } from 'lucide-react';

const AnimatedCounter = ({ target, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * target));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [showTeamImage, setShowTeamImage] = useState(false);

  const highlights = [
    {
      icon: Globe,
      title: 'Canadian Standards',
      description: 'We bring North American quality standards and engineering excellence to every project.',
    },
    {
      icon: Award,
      title: 'UAE Experience',
      description: 'Deep understanding of local requirements, regulations, and market expectations.',
    },
    {
      icon: Target,
      title: 'Professional Execution',
      description: 'Dedicated project management ensuring timely delivery and quality control.',
    },
    {
      icon: Clock,
      title: 'Cost-Effective Solutions',
      description: 'Competitive pricing without compromising on materials or workmanship quality.',
    },
  ];

  const stats = [
    { value: 150, suffix: '+', label: 'Projects Completed' },
    { value: 50, suffix: '+', label: 'Happy Clients' },
    { value: 5, suffix: '+', label: 'Years Experience' },
  ];

  return (
    <section 
      id="about" 
      className="py-24 md:py-32 px-6 relative"
      data-testid="about-section"
      ref={ref}
    >
      {/* Glassy background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white/60 backdrop-blur-sm"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Team & Office Images Section */}
        <motion.div 
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Team Image */}
            <motion.div
              className="relative overflow-hidden rounded-sm group cursor-pointer"
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              onClick={() => setShowTeamImage(true)}
              data-testid="team-image-container"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/81gvkj0k_WhatsApp%20Image%202026-03-27%20at%201.37.26%20PM.jpeg"
                alt="Glow Space Team with branded vehicles"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] font-medium">Our Team</span>
                <h3 className="text-xl font-medium text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Professional & Dedicated Staff
                </h3>
              </div>
              {/* Zoom Icon */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <ZoomIn size={20} className="text-white" />
                </div>
              </div>
            </motion.div>

            {/* Office Image */}
            <motion.div
              className="relative overflow-hidden rounded-sm group"
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <img 
                src="https://images.pexels.com/photos/36083790/pexels-photo-36083790.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern office building Dubai"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] font-medium">Our Location</span>
                <h3 className="text-xl font-medium text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Champion Tower, Dubai Sports City
                </h3>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <motion.span 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-600 block mb-4 flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <span className="w-8 h-px bg-gradient-to-r from-sky-500 to-cyan-400"></span>
              About Glow Space
            </motion.span>
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-800 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Building Excellence <br/>
              <span className="text-gradient">Since Day One</span>
            </motion.h2>
            <motion.div 
              className="space-y-4 text-slate-600 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <p>
                Glow Space LLC is an original Canadian company operating in the UAE, bringing together the best of North American engineering standards with deep local expertise. We specialize in premium aluminium and glass solutions for both residential and commercial sectors.
              </p>
              <p>
                Our commitment to quality, combined with competitive pricing, has established us as a trusted partner for property owners, real estate companies, and contractors across Dubai and the Emirates.
              </p>
            </motion.div>

            {/* Animated Stats */}
            <motion.div 
              className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-slate-200/50"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              {stats.map((stat, index) => (
                <motion.div 
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div 
                    className="text-3xl sm:text-4xl font-light text-slate-800" 
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Highlights Grid */}
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {highlights.map((item, index) => (
              <motion.div
                key={item.title}
                className="glass-card p-6 rounded-sm group cursor-pointer"
                data-testid={`about-highlight-${index}`}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                whileHover={{ 
                  scale: 1.03, 
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  y: -5
                }}
              >
                <motion.div 
                  className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-cyan-100 border border-white/50 flex items-center justify-center mb-4 group-hover:from-sky-200 group-hover:to-cyan-200 transition-all duration-300 shadow-sm"
                  whileHover={{ rotate: 10 }}
                >
                  <item.icon size={24} className="text-sky-600 group-hover:text-[#25D366] transition-colors" />
                </motion.div>
                <h3 
                  className="text-lg font-medium text-slate-800 mb-2 group-hover:text-slate-900 transition-colors"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed group-hover:text-slate-700 transition-colors">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Team Image Lightbox */}
      <AnimatePresence>
        {showTeamImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTeamImage(false)}
            data-testid="team-lightbox"
          >
            {/* Close button */}
            <button
              className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={() => setShowTeamImage(false)}
              data-testid="team-lightbox-close"
            >
              <X size={24} />
            </button>

            {/* Image container */}
            <motion.div
              className="relative max-w-6xl max-h-[90vh] w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/81gvkj0k_WhatsApp%20Image%202026-03-27%20at%201.37.26%20PM.jpeg"
                alt="Glow Space Team with branded vehicles"
                className="w-full h-full object-contain rounded-sm"
              />
              
              {/* Image info */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-sm">
                <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] mb-2 block font-medium">
                  Our Team
                </span>
                <h3 
                  className="text-xl font-medium text-white mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Professional & Dedicated Staff
                </h3>
                <p className="text-slate-300 text-sm">
                  Our experienced team with branded fleet ready to serve across the UAE
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default About;
